import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';
import { Download } from 'lucide-react';

type NamedRef = { _id: string; name: string; };
type UserRef = { _id: string; firstName: string; lastName: string; };

interface QuoteItem {
  description: string;
  quantity: number;
  unitPrice: number;
  itemType: 'equipment' | 'product' | 'service';
  equipmentId?: NamedRef;
  productId?: NamedRef;
}

interface Quote {
  _id: string;
  title: string;
  reference: string;
  description: string;
  status: string;
  validUntil?: string;
  clientId: UserRef;
  technicianId?: UserRef;
  items: QuoteItem[];
  subtotal: number;
  discountPercentage: number;
  discountAmount?: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  notes?: string;
  terms?: string;
}

const ViewQuote: React.FC = () => {
  const params = useParams();
  const quoteId = (params as any)?.id || (params as any)?.quoteId;
  const [quote, setQuote] = useState<Quote | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchQuote = async () => {
      if (!quoteId) {
        toast.error('Quote ID is missing.');
        setIsLoading(false);
        return;
      }
      try {
        const res = await apiService.quotes.getById(quoteId);
        const apiQuote =
          res?.data?.data?.quote ??
          res?.data?.quote ??
          res?.data?.data ??
          res?.data;

        if (!apiQuote) {
          throw new Error('Quote not found in API response');
        }
        setQuote(apiQuote);
      } catch (error) {
        console.error("Failed to fetch quote:", error);
        toast.error("Erreur lors du chargement du devis.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuote();
  }, [quoteId]);

  if (isLoading || !quote) return <Layout><div className="p-6">Chargement…</div></Layout>;

  return (
    <Layout>
      <Card className="p-6 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold">{quote.title}</h1>
            <span className="text-sm px-2 py-1 rounded bg-gray-100">{quote.reference}</span>
          </div>
          <Button
            onClick={() => window.open(`/api/quotes/${quote._id}/pdf`, '_blank')}
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger PDF
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><b>Client:</b> {quote.clientId?.firstName} {quote.clientId?.lastName}</div>
          <div><b>Technicien:</b> {quote.technicianId ? `${quote.technicianId.firstName} ${quote.technicianId.lastName}` : '—'}</div>
          <div><b>Statut:</b> {quote.status}</div>
          <div><b>Valide jusqu’au:</b> {quote.validUntil ? new Date(quote.validUntil).toLocaleDateString() : '—'}</div>
        </div>

        <p className="text-gray-700">{quote.description}</p>

        <div>
          <h2 className="font-semibold mt-4 mb-2">Articles</h2>
          <ul className="divide-y rounded border">
            {quote.items.map((it, i) => (
              <li key={i} className="p-3 flex justify-between">
                <div>
                  <div className="font-medium">{it.description}</div>
                  <div className="text-sm text-gray-500">
                    {it.itemType === 'equipment' && it.equipmentId?.name}
                    {it.itemType === 'product' && it.productId?.name}
                    {it.itemType === 'service' && 'Prestation'}
                  </div>
                </div>
                <div className="text-right">
                  <div>{it.quantity} × {Number(it.unitPrice).toFixed(2)} €</div>
                  <div className="font-semibold">{(it.quantity * it.unitPrice).toFixed(2)} €</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-gray-50 p-4 rounded">
          <div className="flex justify-between"><span>Sous-total</span><b>{Number(quote.subtotal).toFixed(2)} €</b></div>
          <div className="flex justify-between"><span>Remise</span><b>
            {quote.discountPercentage > 0 ? `${quote.discountPercentage}%` : `${Number(quote.discountAmount || 0).toFixed(2)} €`}
          </b></div>
          <div className="flex justify-between"><span>TVA ({quote.taxRate}%)</span><b>{Number(quote.taxAmount).toFixed(2)} €</b></div>
          <div className="flex justify-between text-lg border-t pt-2"><span>Total</span><b>{Number(quote.total).toFixed(2)} €</b></div>
        </div>

        {quote.notes && <div><b>Notes:</b> {quote.notes}</div>}
        {quote.terms && <div><b>Conditions:</b> {quote.terms}</div>}
      </Card>
    </Layout>
  );
}

export default ViewQuote;
